import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMainStore } from "@/stores";
import {
  deleteSavedProspectSearchApi,
  fetchSavedProspectSearches,
  saveProspectSearchApi,
} from "@/features/contacts/api/apolloApi";
import { showErrorFromMutation } from "@/shared/utils/toast";
import type { SavedSearchItem } from "../types";

interface SavedSearchRow {
  id: string;
  name: string;
  query: string;
  campaign_id: string | null;
  campaign_name: string | null;
  result_count: number;
  last_run_at: string;
}

const toItem = (row: SavedSearchRow): SavedSearchItem => ({
  id: row.id,
  name: row.name,
  query: row.query,
  campaignId: row.campaign_id,
  campaignName: row.campaign_name,
  resultCount: row.result_count,
  created_at: row.last_run_at,
});

export interface SaveSearchInput {
  query?: string;
  campaignId?: string | number;
  campaignName?: string;
  resultCount: number;
}

/** The user's saved contact searches, kept in the database so they survive a refresh. */
export function useSavedProspectSearches() {
  const userId = useMainStore((state: any) => state.user?.id);
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ["prospect-saved-searches", userId], [userId]);

  const { data } = useQuery({
    queryKey,
    queryFn: fetchSavedProspectSearches,
    enabled: Boolean(userId),
    staleTime: 60_000,
    meta: { silentError: true },
  });
  const savedSearches: SavedSearchItem[] = useMemo(
    () => (Array.isArray(data) ? (data as SavedSearchRow[]).map(toItem) : []),
    [data],
  );

  const { mutate: persist } = useMutation({
    mutationFn: saveProspectSearchApi,
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
    // Failing to remember a search must never get in the way of the search itself.
    onError: () => undefined,
  });

  const { mutate: remove } = useMutation({
    mutationFn: deleteSavedProspectSearchApi,
    // Drop it from the list straight away; put it back if the server refuses.
    onMutate: async (id: string | number) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<SavedSearchRow[]>(queryKey);
      queryClient.setQueryData<SavedSearchRow[]>(queryKey, (rows) => (rows || []).filter((r) => r.id !== id));
      return { previous };
    },
    onError: (error: any, _id, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
      showErrorFromMutation(error, "Could not delete the saved search");
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const saveSearch = useCallback(
    (input: SaveSearchInput) =>
      persist({
        query: input.query ?? "",
        campaign_id: input.campaignId != null ? String(input.campaignId) : undefined,
        campaign_name: input.campaignName,
        result_count: input.resultCount,
      } as any),
    [persist],
  );

  return { savedSearches, saveSearch, deleteSavedSearch: remove };
}
